import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import i18n from 'i18n-js';
import SectionTitle from '../components/SectionTitle';

import { theme } from 'config/theme';
import { EditorTable } from '../context';
import { useTableMapWizard } from '../context';
import MapEditorCanvas, { ZOOM_MIN, ZOOM_MAX, ZOOM_STEP } from '../components/MapEditorCanvas';
import TableFigure from '../components/TableFigure';
import TableSettingsPanel from '../components/TableSettingsPanel';
import SelectionHighlight from '../components/SelectionHighlight';
import { DineInQrCode, TableMaps } from 'api/controllers/tableMaps';
import {
    clampPointToArea,
    computeRoom,
    EditorArea,
    RoomRect,
    SizeMeters,
    snapPointMeters,
    tableFootprintPx,
} from '../mapGeometry';

const DRAG_THRESHOLD = 4;

// Rotate handle geometry: a stem + circle placed along the current rotation
// angle, reachable beyond the figure regardless of its shape.
const ROTATE_HANDLE_GAP = 26;
const ROTATE_HANDLE_STEM = 20;
const ROTATE_HANDLE_RADIUS = 6;
const ROTATE_SNAP_DEGREES = 15;

function pointAtAngle(deg: number, dist: number): { x: number; y: number } {
    const rad = (deg * Math.PI) / 180;
    return { x: dist * Math.sin(rad), y: -dist * Math.cos(rad) };
}

/** Angle (0..359, clockwise from up) of `point` as seen from the table center. */
function angleFromCenter(dx: number, dy: number, snap: boolean): number {
    const raw = (Math.atan2(dx, -dy) * 180) / Math.PI;
    const normalized = ((raw % 360) + 360) % 360;
    if (!snap) return Math.round(normalized) % 360;
    return (Math.round(normalized / ROTATE_SNAP_DEGREES) * ROTATE_SNAP_DEGREES) % 360;
}

const useStyles = makeStyles({
    root: {
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
    },
    body: {
        display: 'flex',
        gap: 0,
        alignItems: 'flex-start',
    },
    leftSpacer: {
        width: 272,
        flexShrink: 0,
    },
    canvasColumn: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    zoomControls: {
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 4,
        overflow: 'hidden',
    },
    zoomButton: {
        width: 32,
        height: 32,
        border: 'none',
        background: 'white',
        cursor: 'pointer',
        fontSize: 18,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
            backgroundColor: theme.palette.grey['300'],
        },
        '&:disabled': {
            color: theme.palette.grey['400'],
            cursor: 'not-allowed',
        },
    },
    zoomLabel: {
        minWidth: 48,
        textAlign: 'center' as const,
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        borderLeft: `1px solid ${theme.palette.grey['300']}`,
        borderRight: `1px solid ${theme.palette.grey['300']}`,
        lineHeight: '32px',
        userSelect: 'none' as const,
    },
    canvasWrapper: {
        width: '100%',
        height: 440,
        borderRadius: '8px 0 0 8px',
        overflow: 'hidden',
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRight: 'none',
        position: 'relative' as const,
    },
    panelColumn: {
        width: 272,
        flexShrink: 0,
        height: 440,
        // Push the panel down so its top aligns with canvasWrapper (toolbar 32px + gap 12px).
        marginTop: 44,
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: '0 8px 8px 0',
        overflow: 'hidden',
        backgroundColor: 'white',
    },
    emptyPanel: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        textAlign: 'center' as const,
    },
    emptyPanelText: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        lineHeight: 1.5,
    },
});

function clientToSVGPoint(
    svg: SVGSVGElement,
    clientX: number,
    clientY: number
): { x: number; y: number } {
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const inv = ctm.inverse();
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const svgPt = pt.matrixTransform(inv);
    return { x: svgPt.x, y: svgPt.y };
}

/** Clamp a table's normalized-global center (nx,ny) to inside the area shape. Centre only — see report. */
function clampCenterToArea(
    nx: number,
    ny: number,
    size: SizeMeters,
    area: EditorArea
): { x: number; y: number } {
    const metres = { x: nx * size.width, y: ny * size.height };
    const clamped = snapPointMeters(clampPointToArea(metres, area));
    return { x: clamped.x / size.width, y: clamped.y / size.height };
}

interface PlacedTableDetailsProps {
    table: EditorTable;
    room: RoomRect;
    svgRef: React.RefObject<SVGSVGElement>;
    isSelected: boolean;
    onSelect: (id: string) => void;
    onDragEnd: (id: string, x: number, y: number) => void;
    onRotate: (id: string, rotation: number) => void;
}

const PlacedTableDetails = React.memo(function PlacedTableDetails({
    table,
    room,
    svgRef,
    isSelected,
    onSelect,
    onDragEnd,
    onRotate,
}: PlacedTableDetailsProps) {
    const { draft } = useTableMapWizard();
    const gRef = useRef<SVGGElement>(null);

    const dragActive = useRef(false);
    const didDrag = useRef(false);
    const dragStart = useRef({
        startNx: 0,
        startNy: 0,
        startPointerSvgX: 0,
        startPointerSvgY: 0,
    });

    const rotateActive = useRef(false);
    const [dragRotation, setDragRotation] = useState<number | null>(null);
    const rotation = dragRotation ?? table.rotation;

    const { width: figW, height: figH } = tableFootprintPx(
        table.shape,
        table.width,
        table.height,
        draft.tableDefaults,
        room
    );
    const cx = room.x + table.x * room.width;
    const cy = room.y + table.y * room.height;

    const handlePointerDown = useCallback(
        (e: React.PointerEvent<SVGGElement>) => {
            e.stopPropagation();
            if (!svgRef.current) return;
            const gEl = e.currentTarget as SVGGElement;
            gEl.setPointerCapture(e.pointerId);
            const pt = clientToSVGPoint(svgRef.current, e.clientX, e.clientY);
            dragActive.current = true;
            didDrag.current = false;
            dragStart.current = {
                startNx: table.x,
                startNy: table.y,
                startPointerSvgX: pt.x,
                startPointerSvgY: pt.y,
            };
            gEl.style.cursor = 'grabbing';
        },
        [table.x, table.y, svgRef]
    );

    const handlePointerMove = useCallback(
        (e: React.PointerEvent<SVGGElement>) => {
            if (!dragActive.current || !svgRef.current) return;
            const pt = clientToSVGPoint(svgRef.current, e.clientX, e.clientY);
            const dsvgX = pt.x - dragStart.current.startPointerSvgX;
            const dsvgY = pt.y - dragStart.current.startPointerSvgY;

            if (!didDrag.current && Math.hypot(dsvgX, dsvgY) > DRAG_THRESHOLD) {
                didDrag.current = true;
            }

            if (didDrag.current) {
                const dnx = dsvgX / room.width;
                const dny = dsvgY / room.height;
                const clamped = clampCenterToArea(
                    dragStart.current.startNx + dnx,
                    dragStart.current.startNy + dny,
                    draft.size,
                    draft.area
                );
                if (gRef.current) {
                    const newCx = room.x + clamped.x * room.width;
                    const newCy = room.y + clamped.y * room.height;
                    gRef.current.setAttribute('transform', `translate(${newCx}, ${newCy})`);
                }
            }
        },
        [svgRef, room, draft.size, draft.area]
    );

    const handlePointerUp = useCallback(
        (e: React.PointerEvent<SVGGElement>) => {
            if (!dragActive.current || !svgRef.current) return;
            const gEl = e.currentTarget as SVGGElement;
            gEl.style.cursor = 'grab';
            dragActive.current = false;

            if (!didDrag.current) {
                onSelect(table.id);
            } else {
                const pt = clientToSVGPoint(svgRef.current, e.clientX, e.clientY);
                const dsvgX = pt.x - dragStart.current.startPointerSvgX;
                const dsvgY = pt.y - dragStart.current.startPointerSvgY;
                const dnx = dsvgX / room.width;
                const dny = dsvgY / room.height;
                const clamped = clampCenterToArea(
                    dragStart.current.startNx + dnx,
                    dragStart.current.startNy + dny,
                    draft.size,
                    draft.area
                );
                onDragEnd(table.id, clamped.x, clamped.y);
            }
        },
        [svgRef, room, draft.size, draft.area, table.id, onSelect, onDragEnd]
    );

    const handleRotateDown = useCallback((e: React.PointerEvent<SVGCircleElement>) => {
        e.stopPropagation();
        (e.currentTarget as SVGCircleElement).setPointerCapture(e.pointerId);
        rotateActive.current = true;
    }, []);

    const handleRotateMove = useCallback(
        (e: React.PointerEvent<SVGCircleElement>) => {
            if (!rotateActive.current || !svgRef.current) return;
            const pt = clientToSVGPoint(svgRef.current, e.clientX, e.clientY);
            setDragRotation(angleFromCenter(pt.x - cx, pt.y - cy, !e.shiftKey));
        },
        [svgRef, cx, cy]
    );

    const handleRotateUp = useCallback(
        (e: React.PointerEvent<SVGCircleElement>) => {
            if (!rotateActive.current || !svgRef.current) return;
            rotateActive.current = false;
            const pt = clientToSVGPoint(svgRef.current, e.clientX, e.clientY);
            const angle = angleFromCenter(pt.x - cx, pt.y - cy, !e.shiftKey);
            setDragRotation(null);
            onRotate(table.id, angle);
        },
        [svgRef, cx, cy, table.id, onRotate]
    );

    const handleRotateCancel = useCallback(() => {
        rotateActive.current = false;
        setDragRotation(null);
    }, []);

    const figHalf = Math.max(figW, figH) / 2;
    const stemStart = pointAtAngle(rotation, figHalf + ROTATE_HANDLE_GAP);
    const handlePoint = pointAtAngle(rotation, figHalf + ROTATE_HANDLE_GAP + ROTATE_HANDLE_STEM);

    return (
        <g
            ref={gRef}
            transform={`translate(${cx}, ${cy})`}
            style={{ cursor: 'grab' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
        >
            <TableFigure
                width={figW}
                height={figH}
                seats={table.seats}
                shape={table.shape}
                label={table.label}
                subLabel={String(table.seats)}
                isActive={table.isActive}
                rotation={rotation}
            />
            {isSelected && <SelectionHighlight figW={figW} figH={figH} />}
            {isSelected && (
                <>
                    <line
                        x1={stemStart.x}
                        y1={stemStart.y}
                        x2={handlePoint.x}
                        y2={handlePoint.y}
                        stroke={theme.palette.primary.main}
                        strokeWidth={1.5}
                    />
                    <circle
                        cx={handlePoint.x}
                        cy={handlePoint.y}
                        r={ROTATE_HANDLE_RADIUS}
                        fill="white"
                        stroke={theme.palette.primary.main}
                        strokeWidth={2}
                        style={{ cursor: 'grab' }}
                        onPointerDown={handleRotateDown}
                        onPointerMove={handleRotateMove}
                        onPointerUp={handleRotateUp}
                        onPointerCancel={handleRotateCancel}
                    />
                </>
            )}
        </g>
    );
});

interface TableDetailsStepProps {
    originalTables: EditorTable[];
}

export default function TableDetailsStep({ originalTables }: TableDetailsStepProps) {
    const classes = useStyles();
    const { draft, dispatch } = useTableMapWizard();
    const room = useMemo(() => computeRoom(draft.size), [draft.size.width, draft.size.height]);

    const [zoom, setZoom] = useState(1.0);
    const svgRef = useRef<SVGSVGElement>(null);
    const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
    const [qrCodes, setQrCodes] = useState<DineInQrCode[]>([]);

    useEffect(() => {
        TableMaps.getDineInQrCodes().then(setQrCodes);
    }, []);

    useEffect(() => {
        if (selectedTableId !== null) {
            const still = draft.tables.some((t) => t.id === selectedTableId);
            if (!still) setSelectedTableId(null);
        }
    }, [draft.tables, selectedTableId]);

    const selectedTable = selectedTableId
        ? draft.tables.find((t) => t.id === selectedTableId) ?? null
        : null;

    const otherUsedQrCodes = useMemo(
        () =>
            draft.tables
                .filter((t) => t.id !== selectedTableId && !!t.qrCode && t.isActive)
                .map((t) => t.qrCode as string),
        [draft.tables, selectedTableId]
    );

    const otherTableNames = useMemo(
        () =>
            draft.tables
                .filter((t) => t.id !== selectedTableId)
                .map((t) => t.label.trim().toLowerCase()),
        [draft.tables, selectedTableId]
    );

    const originalActiveQrCodes = useMemo(
        () => originalTables.filter((t) => t.isActive && !!t.qrCode).map((t) => t.qrCode as string),
        [originalTables]
    );

    const handleSelect = useCallback((id: string) => {
        setSelectedTableId(id);
    }, []);

    const handleDragEnd = useCallback(
        (id: string, x: number, y: number) => {
            dispatch({ type: 'UPDATE_TABLE_POSITION', payload: { id, x, y } });
        },
        [dispatch]
    );

    const handleRotate = useCallback(
        (id: string, rotation: number) => {
            dispatch({ type: 'UPDATE_TABLE', payload: { id, rotation } });
        },
        [dispatch]
    );

    const handleSave = useCallback(
        (patch: Partial<Omit<EditorTable, 'id'>>) => {
            if (!selectedTableId) return;
            dispatch({ type: 'UPDATE_TABLE', payload: { id: selectedTableId, ...patch } });
            setSelectedTableId(null);
        },
        [dispatch, selectedTableId]
    );

    const handleDelete = useCallback(() => {
        if (!selectedTableId) return;
        dispatch({ type: 'REMOVE_TABLE', payload: { id: selectedTableId } });
        setSelectedTableId(null);
    }, [dispatch, selectedTableId]);

    function handleZoomIn() {
        setZoom((prev) => Math.min(ZOOM_MAX, parseFloat((prev + ZOOM_STEP).toFixed(2))));
    }

    function handleZoomOut() {
        setZoom((prev) => Math.max(ZOOM_MIN, parseFloat((prev - ZOOM_STEP).toFixed(2))));
    }

    const zoomPercent = Math.round(zoom * 100);

    const tablesLayer = useMemo(
        () =>
            draft.tables.map((table) => (
                <PlacedTableDetails
                    key={table.id}
                    table={table}
                    room={room}
                    svgRef={svgRef}
                    isSelected={table.id === selectedTableId}
                    onSelect={handleSelect}
                    onDragEnd={handleDragEnd}
                    onRotate={handleRotate}
                />
            )),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [draft.tables, room, selectedTableId, handleSelect, handleDragEnd, handleRotate]
    );

    return (
        <div className={classes.root}>
            <SectionTitle>{i18n.t('tableMaps.wizard.tableDetails')}</SectionTitle>

            <div className={classes.body}>
                <div className={classes.leftSpacer} />
                <div className={classes.canvasColumn}>
                    <div className={classes.toolbar}>
                        <div className={classes.zoomControls}>
                            <button
                                className={classes.zoomButton}
                                onClick={handleZoomOut}
                                disabled={zoom <= ZOOM_MIN}
                                type="button"
                                aria-label="Zoom out"
                            >
                                −
                            </button>
                            <span className={classes.zoomLabel}>{zoomPercent}%</span>
                            <button
                                className={classes.zoomButton}
                                onClick={handleZoomIn}
                                disabled={zoom >= ZOOM_MAX}
                                type="button"
                                aria-label="Zoom in"
                            >
                                +
                            </button>
                        </div>
                    </div>

                    <div className={classes.canvasWrapper}>
                        <MapEditorCanvas
                            gridEnabled={draft.gridEnabled}
                            zoom={zoom}
                            size={draft.size}
                            area={draft.area}
                            entrance={draft.entrance}
                            isMarkingEntrance={false}
                            onEntranceChange={() => undefined}
                            svgRef={svgRef}
                        >
                            <rect
                                x={room.x}
                                y={room.y}
                                width={room.width}
                                height={room.height}
                                fill="transparent"
                                onPointerDown={() => {
                                    setSelectedTableId(null);
                                }}
                            />
                            {tablesLayer}
                        </MapEditorCanvas>
                    </div>
                </div>

                <div className={classes.panelColumn}>
                    {selectedTable ? (
                        <TableSettingsPanel
                            table={selectedTable}
                            qrCodes={qrCodes}
                            otherUsedQrCodes={otherUsedQrCodes}
                            otherTableNames={otherTableNames}
                            originalActiveQrCodes={originalActiveQrCodes}
                            onSave={handleSave}
                            onDelete={handleDelete}
                            onRotate={(rotation) => handleRotate(selectedTable.id, rotation)}
                        />
                    ) : (
                        <div className={classes.emptyPanel}>
                            <Typography className={classes.emptyPanelText}>
                                {i18n.t('tableMaps.wizard.selectTableHint')}
                            </Typography>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
